import { BackdropLoading } from '@/components/backdrop-loading'
import ConfirmModal from '@/components/confirm-modal'
import { Container, Content } from '@/components/container/container'
import {
    createAgendaSetting,
    deleteAgendaSetting,
    getAgenda,
    getAgendaSettingsByAgenda,
    updateAgenda,
    updateAgendaSetting,
} from '@/lib/api/agenda/agenda-api'
import {
    Agenda,
    AgendaSetting,
} from '@/lib/api/common/interfaces/agenda.interface'
import { useEffect, useState } from 'react'
import {
    MdAdd,
    MdArrowBack,
    MdCalendarMonth,
    MdClose,
    MdDelete,
    MdEdit,
    MdEventRepeat,
    MdSave,
    MdSchedule,
    MdTimer,
    MdGroups,
} from 'react-icons/md'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'react-toastify'

interface WorkingHours {
    [key: string]: { start: string; end: string; enabled: boolean }
}

const DAYS_OF_WEEK = [
    { key: 'monday', label: 'Segunda-feira' },
    { key: 'tuesday', label: 'Terça-feira' },
    { key: 'wednesday', label: 'Quarta-feira' },
    { key: 'thursday', label: 'Quinta-feira' },
    { key: 'friday', label: 'Sexta-feira' },
    { key: 'saturday', label: 'Sábado' },
    { key: 'sunday', label: 'Domingo' },
]

const SLOT_DURATIONS = [
    { value: 15, label: '15 minutos' },
    { value: 20, label: '20 minutos' },
    { value: 30, label: '30 minutos' },
    { value: 45, label: '45 minutos' },
    { value: 60, label: '1 hora' },
    { value: 90, label: '1h30' },
    { value: 120, label: '2 horas' },
]

const DEFAULT_WORKING_HOURS: WorkingHours = {
    monday: { start: '08:00', end: '18:00', enabled: true },
    tuesday: { start: '08:00', end: '18:00', enabled: true },
    wednesday: { start: '08:00', end: '18:00', enabled: true },
    thursday: { start: '08:00', end: '18:00', enabled: true },
    friday: { start: '08:00', end: '18:00', enabled: true },
    saturday: { start: '08:00', end: '12:00', enabled: false },
    sunday: { start: '08:00', end: '12:00', enabled: false },
}

export default function AgendaSettingsPage() {
    const { agendaId } = useParams<{ agendaId: string }>()
    const navigate = useNavigate()
    const [loading, setLoading] = useState(false)
    const [loadingAgenda, setLoadingAgenda] = useState(false)
    const [saving, setSaving] = useState(false)
    const [settings, setSettings] = useState<AgendaSetting[]>([])
    const [agenda, setAgenda] = useState<Agenda | null>(null)
    const [editingId, setEditingId] = useState<number | null>(null)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [settingToDelete, setSettingToDelete] = useState<number | null>(null)
    const [showAddForm, setShowAddForm] = useState(false)

    // Main agenda settings
    const [slotDuration, setSlotDuration] = useState(30)
    const [bufferTime, setBufferTime] = useState(0)
    const [bufferTimeUnit, setBufferTimeUnit] = useState<'min' | 'h'>('min')
    const [maxOverbooking, setMaxOverbooking] = useState(0)
    const [arrivalOrder, setArrivalOrder] = useState(false)
    const [workingHours, setWorkingHours] = useState<WorkingHours>(
        DEFAULT_WORKING_HOURS
    )

    const [editForm, setEditForm] = useState({
        name: '',
        value: '',
    })

    const [addForm, setAddForm] = useState({
        name: '',
        value: '',
    })

    useEffect(() => {
        if (agendaId) {
            loadAgenda()
            loadSettings()
        }
    }, [agendaId])

    useEffect(() => {
        if (agenda) {
            setSlotDuration(agenda.slotDuration || 30)
            const bt = agenda.bufferTime || 0
            if (bt > 0 && bt % 60 === 0) {
                setBufferTime(bt / 60)
                setBufferTimeUnit('h')
            } else {
                setBufferTime(bt)
                setBufferTimeUnit('min')
            }
            setMaxOverbooking(agenda.maxOverbooking || 0)
            setArrivalOrder(agenda.arrivalOrder === true)
            if (agenda.workingHours) {
                try {
                    const parsed = JSON.parse(agenda.workingHours)
                    setWorkingHours({ ...DEFAULT_WORKING_HOURS, ...parsed })
                } catch {
                    setWorkingHours(DEFAULT_WORKING_HOURS)
                }
            }
        }
    }, [agenda])

    const loadAgenda = async () => {
        if (!agendaId) return

        setLoadingAgenda(true)
        try {
            const response = await getAgenda(parseInt(agendaId))
            if (response.success) {
                setAgenda(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar agenda')
                navigate('/atendimento/agenda')
            }
        } catch (error) {
            toast.error('Erro ao carregar agenda')
            navigate('/atendimento/agenda')
        } finally {
            setLoadingAgenda(false)
        }
    }

    const loadSettings = async () => {
        if (!agendaId) return

        setLoading(true)
        try {
            const response = await getAgendaSettingsByAgenda(parseInt(agendaId))
            if (response.success) {
                setSettings(response.result)
            } else {
                toast.error(response.error || 'Erro ao carregar configurações')
            }
        } catch (error) {
            toast.error('Erro ao carregar configurações')
        } finally {
            setLoading(false)
        }
    }

    const handleSaveMainSettings = async () => {
        if (!agendaId) return

        setSaving(true)
        try {
            const bufferTimeInMinutes = bufferTimeUnit === 'h' ? bufferTime * 60 : bufferTime
            const response = await updateAgenda(parseInt(agendaId), {
                slotDuration,
                bufferTime: bufferTimeInMinutes,
                maxOverbooking,
                arrivalOrder,
                workingHours: JSON.stringify(workingHours),
            })

            if (response.success) {
                toast.success('Configurações salvas com sucesso')
                setAgenda(response.result)
            } else {
                toast.error(response.error || 'Erro ao salvar configurações')
            }
        } catch (error) {
            toast.error('Erro ao salvar configurações')
        } finally {
            setSaving(false)
        }
    }

    const handleWorkingHoursChange = (
        day: string,
        field: 'start' | 'end' | 'enabled',
        value: string | boolean
    ) => {
        setWorkingHours(prev => ({
            ...prev,
            [day]: {
                ...prev[day],
                [field]: value,
            },
        }))
    }

    const handleEdit = (setting: AgendaSetting) => {
        setEditingId(setting.id)
        setEditForm({
            name: setting.name,
            value: setting.value,
        })
    }

    const handleCancelEdit = () => {
        setEditingId(null)
        setEditForm({ name: '', value: '' })
    }

    const handleSaveEdit = async () => {
        if (!editingId || !agendaId) return

        if (!editForm.name.trim() || !editForm.value.trim()) {
            toast.error('Nome e valor são obrigatórios')
            return
        }

        setLoading(true)
        try {
            const response = await updateAgendaSetting(editingId, {
                name: editForm.name.trim(),
                value: editForm.value.trim(),
            })

            if (response.success) {
                toast.success('Configuração atualizada com sucesso')
                setEditingId(null)
                setEditForm({ name: '', value: '' })
                loadSettings()
            } else {
                toast.error(response.error || 'Erro ao atualizar configuração')
            }
        } catch (error) {
            toast.error('Erro ao atualizar configuração')
        } finally {
            setLoading(false)
        }
    }

    const handleDelete = (id: number) => {
        setSettingToDelete(id)
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        if (!settingToDelete) return

        setLoading(true)
        try {
            const response = await deleteAgendaSetting(settingToDelete)
            if (response.success) {
                toast.success('Configuração removida com sucesso')
                setShowDeleteModal(false)
                setSettingToDelete(null)
                loadSettings()
            } else {
                toast.error(response.error || 'Erro ao remover configuração')
            }
        } catch (error) {
            toast.error('Erro ao remover configuração')
        } finally {
            setLoading(false)
        }
    }

    const handleAdd = async () => {
        if (!agendaId) return

        if (!addForm.name.trim() || !addForm.value.trim()) {
            toast.error('Nome e valor são obrigatórios')
            return
        }

        setLoading(true)
        try {
            const response = await createAgendaSetting({
                agendaId: parseInt(agendaId),
                name: addForm.name.trim(),
                value: addForm.value.trim(),
            })

            if (response.success) {
                toast.success('Configuração criada com sucesso')
                setAddForm({ name: '', value: '' })
                setShowAddForm(false)
                loadSettings()
            } else {
                toast.error(response.error || 'Erro ao criar configuração')
            }
        } catch (error) {
            toast.error('Erro ao criar configuração')
        } finally {
            setLoading(false)
        }
    }

    return (
        <Container>
            <div className="mb-5 flex items-center gap-3">
                <button
                    className="btn btn-ghost"
                    onClick={() => navigate('/atendimento/agenda')}
                >
                    <MdArrowBack className="text-lg" />
                    Voltar
                </button>
                <h1 className="text-2xl font-bold">
                    Configurações da Agenda
                    <span className="text-base font-normal text-base-content/70 ml-2">
                        {agenda?.type === 'PERSONAL' && '- Pessoal'}
                        {agenda?.type === 'COMPANY' && '- Empresa'}
                        {agenda?.type === 'PROFESSIONAL' && agenda?.professional && `- ${agenda.professional.name}`}
                    </span>
                </h1>
            </div>

            {/* Main Settings */}
            <Content className="p-6 shadow-2xl bg-white rounded-xl mb-6">
                <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
                    <MdSchedule className="text-xl text-secondary" />
                    Configurações de Agendamento
                </h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                    {/* Slot Duration */}
                    <div className="form-control">
                        <label className="label">
                            <span className="label-text font-medium flex items-center gap-2">
                                <MdTimer className="text-base-content/60" />
                                Duração do Slot
                            </span>
                        </label>
                        <select
                            className="select select-bordered w-full"
                            value={slotDuration}
                            onChange={e =>
                                setSlotDuration(parseInt(e.target.value))
                            }
                        >
                            {SLOT_DURATIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>
                                    {opt.label}
                                </option>
                            ))}
                        </select>
                        <label className="label">
                            <span className="label-text-alt text-base-content/60 whitespace-normal">
                                Intervalo padrão entre horários disponíveis
                            </span>
                        </label>
                    </div>

                    {/* Buffer Time */}
                    <div className="form-control">
                        <label className="label">
                            <span className="label-text font-medium flex items-center gap-2">
                                <MdEventRepeat className="text-base-content/60" />
                                Tempo de Intervalo
                            </span>
                        </label>
                        <div className="join w-full">
                            <input
                                type="number"
                                className="input !rounded-none join-item w-full"
                                value={bufferTime}
                                onChange={e =>
                                    setBufferTime(
                                        Math.max(0, parseInt(e.target.value) || 0)
                                    )
                                }
                                min={0}
                                max={bufferTimeUnit === 'h' ? 24 : 60}
                            />
                            <select
                                className="select !rounded-none join-item !max-w-20"
                                value={bufferTimeUnit}
                                onChange={e => {
                                    const newUnit = e.target.value as 'min' | 'h'
                                    if (newUnit === 'h' && bufferTimeUnit === 'min') {
                                        setBufferTime(Math.floor(bufferTime / 60))
                                    } else if (newUnit === 'min' && bufferTimeUnit === 'h') {
                                        setBufferTime(bufferTime * 60)
                                    }
                                    setBufferTimeUnit(newUnit)
                                }}
                            >
                                <option value="min">min</option>
                                <option value="h">horas</option>
                            </select>
                        </div>
                        <label className="label">
                            <span className="label-text-alt text-base-content/60 whitespace-normal">
                                Tempo mínimo entre agendamentos
                            </span>
                        </label>
                    </div>

                    {/* Max Overbooking */}
                    <div className="form-control">
                        <label className="label">
                            <span className="label-text font-medium flex items-center gap-2">
                                <MdCalendarMonth className="text-base-content/60" />
                                Máximo de Encaixes
                            </span>
                        </label>
                        <input
                            type="number"
                            className="input input-bordered w-full"
                            value={maxOverbooking}
                            onChange={e =>
                                setMaxOverbooking(
                                    Math.max(0, parseInt(e.target.value) || 0)
                                )
                            }
                            min={0}
                            max={10}
                        />
                        <label className="label">
                            <span className="label-text-alt text-base-content/60 whitespace-normal">
                                Quantidade máxima de encaixes por horário (0 =
                                desabilitado)
                            </span>
                        </label>
                    </div>

                    {/* Atendimento por ordem de chegada */}
                    <div className="form-control min-w-0">
                        <label className="label">
                            <span className="label-text font-medium flex items-center gap-2">
                                <MdGroups className="text-base-content/60" />
                                Ordem de Chegada
                            </span>
                        </label>
                        <label className="cursor-pointer flex items-center gap-3 h-12 px-4 rounded-lg border border-base-300 bg-base-100">
                            <input
                                type="checkbox"
                                className="toggle toggle-secondary"
                                checked={arrivalOrder}
                                onChange={e =>
                                    setArrivalOrder(e.target.checked)
                                }
                            />
                            <span className="text-sm">
                                {arrivalOrder ? 'Ativado' : 'Desativado'}
                            </span>
                        </label>
                        <label className="label">
                            <span className="label-text-alt text-base-content/60 whitespace-normal">
                                Atende na ordem de chegada
                            </span>
                        </label>
                    </div>
                </div>

                {/* Working Hours */}
                <div className="mb-6">
                    <h3 className="font-medium mb-3 flex items-center gap-2">
                        <MdSchedule className="text-base-content/60" />
                        Horários de Trabalho
                    </h3>
                    <div className="bg-base-200 rounded-lg p-4">
                        <div className="grid gap-3">
                            {DAYS_OF_WEEK.map(day => (
                                <div
                                    key={day.key}
                                    className="flex flex-wrap items-center gap-3"
                                >
                                    <div className="w-32">
                                        <label className="label cursor-pointer justify-start gap-2 p-0">
                                            <input
                                                type="checkbox"
                                                className="toggle toggle-sm toggle-secondary"
                                                checked={
                                                    workingHours[day.key]?.enabled
                                                }
                                                onChange={e =>
                                                    handleWorkingHoursChange(
                                                        day.key,
                                                        'enabled',
                                                        e.target.checked
                                                    )
                                                }
                                            />
                                            <span
                                                className={`text-sm ${!workingHours[day.key]?.enabled ? 'text-base-content/50' : ''}`}
                                            >
                                                {day.label}
                                            </span>
                                        </label>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="time"
                                            className="input input-bordered input-sm w-28"
                                            value={
                                                workingHours[day.key]?.start ||
                                                '08:00'
                                            }
                                            onChange={e =>
                                                handleWorkingHoursChange(
                                                    day.key,
                                                    'start',
                                                    e.target.value
                                                )
                                            }
                                            disabled={
                                                !workingHours[day.key]?.enabled
                                            }
                                        />
                                        <span className="text-base-content/60">
                                            até
                                        </span>
                                        <input
                                            type="time"
                                            className="input input-bordered input-sm w-28"
                                            value={
                                                workingHours[day.key]?.end ||
                                                '18:00'
                                            }
                                            onChange={e =>
                                                handleWorkingHoursChange(
                                                    day.key,
                                                    'end',
                                                    e.target.value
                                                )
                                            }
                                            disabled={
                                                !workingHours[day.key]?.enabled
                                            }
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="flex justify-end">
                    <button
                        className="btn btn-secondary"
                        onClick={handleSaveMainSettings}
                        disabled={saving}
                    >
                        {saving ? (
                            <span className="loading loading-spinner loading-sm"></span>
                        ) : (
                            <MdSave className="text-lg" />
                        )}
                        Salvar Configurações
                    </button>
                </div>
            </Content>

            {/* Custom Settings */}
            <Content className="p-6 shadow-2xl bg-white rounded-xl">
                <div className="flex items-center justify-between mb-4">
                    <h2 className="text-lg font-semibold">
                        Configurações Personalizadas
                    </h2>
                    <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => setShowAddForm(true)}
                    >
                        <MdAdd className="text-lg" />
                        Nova
                    </button>
                </div>

                <p className="text-sm text-base-content/60 mb-4">
                    Configurações personalizadas para uso com IA e automações.
                    Escreva claramente para que a IA entenda.
                </p>

                {showAddForm && (
                    <div className="mb-4 p-4 border border-base-300 rounded-lg bg-base-200">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-semibold">Nova Configuração</h3>
                            <button
                                className="btn btn-ghost btn-sm"
                                onClick={() => {
                                    setShowAddForm(false)
                                    setAddForm({ name: '', value: '' })
                                }}
                            >
                                <MdClose />
                            </button>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="label">
                                    <span className="label-text">Nome</span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Ex: Total de agendamentos diários"
                                    value={addForm.name}
                                    onChange={e =>
                                        setAddForm({
                                            ...addForm,
                                            name: e.target.value,
                                        })
                                    }
                                />
                            </div>
                            <div>
                                <label className="label">
                                    <span className="label-text">Valor</span>
                                </label>
                                <input
                                    type="text"
                                    className="input input-bordered w-full"
                                    placeholder="Ex: 10"
                                    value={addForm.value}
                                    onChange={e =>
                                        setAddForm({
                                            ...addForm,
                                            value: e.target.value,
                                        })
                                    }
                                />
                            </div>
                        </div>
                        <div className="mt-4 flex justify-end gap-2">
                            <button
                                className="btn btn-ghost"
                                onClick={() => {
                                    setShowAddForm(false)
                                    setAddForm({ name: '', value: '' })
                                }}
                            >
                                Cancelar
                            </button>
                            <button
                                className="btn btn-secondary"
                                onClick={handleAdd}
                            >
                                <MdSave />
                                Salvar
                            </button>
                        </div>
                    </div>
                )}

                {settings.length === 0 ? (
                    <div className="text-center py-8">
                        <p className="text-base-content/70">
                            Nenhuma configuração personalizada
                        </p>
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="table table-zebra w-full">
                            <thead>
                                <tr>
                                    <th>Nome</th>
                                    <th>Valor</th>
                                    <th className="text-right">Ações</th>
                                </tr>
                            </thead>
                            <tbody>
                                {settings.map(setting => (
                                    <tr key={setting.id}>
                                        {editingId === setting.id ? (
                                            <>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className="input input-bordered input-sm w-full"
                                                        value={editForm.name}
                                                        onChange={e =>
                                                            setEditForm({
                                                                ...editForm,
                                                                name: e.target
                                                                    .value,
                                                            })
                                                        }
                                                    />
                                                </td>
                                                <td>
                                                    <input
                                                        type="text"
                                                        className="input input-bordered input-sm w-full"
                                                        value={editForm.value}
                                                        onChange={e =>
                                                            setEditForm({
                                                                ...editForm,
                                                                value: e.target
                                                                    .value,
                                                            })
                                                        }
                                                    />
                                                </td>
                                                <td>
                                                    <div className="flex justify-end gap-2">
                                                        <button
                                                            className="btn btn-ghost btn-sm"
                                                            onClick={
                                                                handleCancelEdit
                                                            }
                                                        >
                                                            <MdClose />
                                                        </button>
                                                        <button
                                                            className="btn btn-secondary btn-sm"
                                                            onClick={
                                                                handleSaveEdit
                                                            }
                                                        >
                                                            <MdSave />
                                                        </button>
                                                    </div>
                                                </td>
                                            </>
                                        ) : (
                                            <>
                                                <td>
                                                    <span className="font-medium">
                                                        {setting.name}
                                                    </span>
                                                </td>
                                                <td>
                                                    <span className="text-base-content/70">
                                                        {setting.value}
                                                    </span>
                                                </td>
                                                <td>
                                                    <div className="flex justify-end gap-2">
                                                        <button
                                                            className="btn btn-ghost btn-sm"
                                                            onClick={() =>
                                                                handleEdit(
                                                                    setting
                                                                )
                                                            }
                                                        >
                                                            <MdEdit />
                                                        </button>
                                                        <button
                                                            className="btn btn-ghost btn-sm text-error"
                                                            onClick={() =>
                                                                handleDelete(
                                                                    setting.id
                                                                )
                                                            }
                                                        >
                                                            <MdDelete />
                                                        </button>
                                                    </div>
                                                </td>
                                            </>
                                        )}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </Content>

            <ConfirmModal
                isOpen={showDeleteModal}
                onClose={() => {
                    setShowDeleteModal(false)
                    setSettingToDelete(null)
                }}
                onConfirm={confirmDelete}
                title="Confirmar Exclusão"
                message="Tem certeza que deseja remover esta configuração?"
            />

            <BackdropLoading visible={loading || loadingAgenda || saving} />
        </Container>
    )
}
